import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { useCreateDocument } from "./use-documents";
import { useDocumentTypes } from "../document-types/use-document-types";
import { useLocales } from "../locales/use-locales";
import { MarkdownPreview } from "../shared/MarkdownPreview";
import { useTosRoleScopes } from "../shared/use-tos-role-scopes";
import { roleScopeLabel } from "../shared/role-scopes";
import {
  TOS_TRANSLATION_BODY_MAX,
  TOS_TRANSLATION_TITLE_MAX,
  TOS_VERSION_MAX,
  type SupportedLocale,
  type TosRoleScope,
} from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

/**
 * Multi-translation create form. The default-locale translation is
 * always present and not removable (backend rejects with
 * `TOS_MISSING_CANONICAL_TRANSLATION` otherwise). The operator can
 * add as many other locales as they want; all of them are submitted
 * in a single `POST /api/admin/tos`.
 */
interface DraftTranslation {
  readonly localeCode: string;
  readonly title: string;
  readonly bodyMarkdown: string;
}

export function CreateDocumentDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const documentTypes = useDocumentTypes();
  const locales = useLocales();
  const roleScopes = useTosRoleScopes();
  const create = useCreateDocument();

  const defaultLocale = useMemo(
    () => locales.data?.find((l) => l.isCanonical) ?? null,
    [locales.data],
  );

  const [roleScope, setRoleScope] = useState<TosRoleScope>("GUEST");
  const [documentTypeId, setDocumentTypeId] = useState<string>("");
  const [version, setVersion] = useState("");
  // Translations always include the default-locale entry first; the
  // operator can append additional locales. Initialised when the
  // dialog opens and the default locale is known.
  const [translations, setTranslations] = useState<DraftTranslation[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setRoleScope("GUEST");
      setDocumentTypeId(documentTypes.data?.[0]?.id ?? "");
      setVersion("");
      setError(null);
      if (defaultLocale) {
        setTranslations([{ localeCode: defaultLocale.code, title: "", bodyMarkdown: "" }]);
      } else {
        setTranslations([]);
      }
    }
  }, [open, documentTypes.data, defaultLocale]);

  const usedLocaleCodes = useMemo(
    () => new Set(translations.map((tr) => tr.localeCode)),
    [translations],
  );

  const availableLocales = useMemo(
    () => (locales.data ?? []).filter((l) => !usedLocaleCodes.has(l.code)),
    [locales.data, usedLocaleCodes],
  );

  const updateTranslation = (index: number, patch: Partial<DraftTranslation>) => {
    setTranslations((prev) => prev.map((tr, i) => (i === index ? { ...tr, ...patch } : tr)));
  };

  const removeTranslation = (index: number) => {
    setTranslations((prev) => prev.filter((_, i) => i !== index));
  };

  const addTranslation = (locale: SupportedLocale) => {
    setTranslations((prev) => [...prev, { localeCode: locale.code, title: "", bodyMarkdown: "" }]);
  };

  const trimmedVersion = version.trim();
  const everyTranslationValid = translations.every((tr) => {
    const title = tr.title.trim();
    const body = tr.bodyMarkdown.trim();
    return (
      title.length > 0 &&
      title.length <= TOS_TRANSLATION_TITLE_MAX &&
      body.length > 0 &&
      body.length <= TOS_TRANSLATION_BODY_MAX
    );
  });
  const isValid =
    documentTypeId.length > 0 &&
    trimmedVersion.length > 0 &&
    trimmedVersion.length <= TOS_VERSION_MAX &&
    translations.length >= 1 &&
    everyTranslationValid &&
    defaultLocale != null;

  const handleSubmit = async () => {
    if (!isValid || !defaultLocale || create.isPending) return;
    setError(null);
    try {
      await create.mutateAsync({
        roleScope,
        documentTypeId,
        version: trimmedVersion,
        // Backend uses publish to flip effectiveFrom to now(); the
        // create payload's value is a placeholder until then. Send now
        // so the row sorts predictably as "most recently drafted".
        effectiveFrom: new Date().toISOString(),
        translations: translations.map((tr) => ({
          localeCode: tr.localeCode,
          title: tr.title.trim(),
          bodyMarkdown: tr.bodyMarkdown.trim(),
        })),
      });
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !create.isPending && onOpenChange(next)}>
      <DialogContent
        data-testid="compliance-create-document-dialog"
        className="max-h-[90vh] max-w-5xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{t("compliance.documents.create.title")}</DialogTitle>
          <DialogDescription>{t("compliance.documents.create.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!defaultLocale && (
            <p
              data-testid="compliance-create-document-no-canonical"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive"
            >
              {t("compliance.documents.create.noDefaultLocale")}
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="doc-role">{t("compliance.documents.fields.roleScope")}</Label>
              <select
                id="doc-role"
                data-testid="compliance-create-document-role-input"
                value={roleScope}
                onChange={(e) => setRoleScope(e.target.value as TosRoleScope)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {roleScopes.scopes.map((scope) => (
                  <option key={scope} value={scope}>
                    {roleScopeLabel(t, scope)}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doc-type">{t("compliance.documents.fields.documentType")}</Label>
              <select
                id="doc-type"
                data-testid="compliance-create-document-type-input"
                value={documentTypeId}
                onChange={(e) => setDocumentTypeId(e.target.value)}
                className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {documentTypes.data?.map((dt) => (
                  <option key={dt.id} value={dt.id}>
                    {dt.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="doc-version">{t("compliance.documents.fields.version")}</Label>
            <Input
              id="doc-version"
              data-testid="compliance-create-document-version-input"
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              maxLength={TOS_VERSION_MAX}
              placeholder="2026-05-27-v1"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">
                {t("compliance.documents.create.translationsHeading")}
              </h3>
              <AddTranslationButton locales={availableLocales} onPick={addTranslation} />
            </div>
            <p className="text-xs text-muted-foreground">
              {t("compliance.documents.create.translationsHint", {
                defaultLocale: defaultLocale?.code ?? "",
              })}
            </p>

            {translations.map((tr, index) => {
              const isDefault = defaultLocale != null && tr.localeCode === defaultLocale.code;
              return (
                <div
                  key={tr.localeCode}
                  data-testid={`compliance-create-document-translation-${tr.localeCode}`}
                  className="space-y-2 rounded-md border border-border bg-muted/20 p-3"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">
                        {tr.localeCode}
                      </code>
                      {isDefault && (
                        <span className="rounded-full bg-wander-orange/15 px-2 py-0.5 text-xs text-wander-orange ring-1 ring-inset ring-wander-orange/40">
                          {t("compliance.locales.defaultBadge")}
                        </span>
                      )}
                    </div>
                    {!isDefault && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`compliance-remove-translation-${tr.localeCode}`}
                        onClick={() => removeTranslation(index)}
                      >
                        <Trash2 className="h-3 w-3" aria-hidden="true" />
                      </Button>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor={`doc-title-${tr.localeCode}`}>
                      {t("compliance.documents.translations.titleField")}
                    </Label>
                    <Input
                      id={`doc-title-${tr.localeCode}`}
                      data-testid={`compliance-create-document-title-input-${tr.localeCode}`}
                      value={tr.title}
                      onChange={(e) => updateTranslation(index, { title: e.target.value })}
                      maxLength={TOS_TRANSLATION_TITLE_MAX}
                    />
                  </div>

                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="space-y-1.5">
                      <Label htmlFor={`doc-body-${tr.localeCode}`}>
                        {t("compliance.documents.translations.bodyField")}
                      </Label>
                      <Textarea
                        id={`doc-body-${tr.localeCode}`}
                        data-testid={`compliance-create-document-body-input-${tr.localeCode}`}
                        value={tr.bodyMarkdown}
                        onChange={(e) => updateTranslation(index, { bodyMarkdown: e.target.value })}
                        maxLength={TOS_TRANSLATION_BODY_MAX}
                        rows={10}
                        className="font-mono text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t("compliance.documents.translations.preview")}</Label>
                      <MarkdownPreview
                        data-testid={`compliance-create-document-preview-${tr.localeCode}`}
                        value={tr.bodyMarkdown}
                        emptyPlaceholder={t("compliance.documents.translations.previewEmpty")}
                        className="min-h-[16rem]"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {error && (
            <p data-testid="compliance-create-document-error" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-create-document-submit"
            onClick={handleSubmit}
            disabled={!isValid || create.isPending}
          >
            {create.isPending
              ? t("compliance.actions.saving")
              : t("compliance.documents.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddTranslationButtonProps {
  readonly locales: readonly SupportedLocale[];
  readonly onPick: (locale: SupportedLocale) => void;
}

/**
 * Native `<select>` instead of a custom popover. The project's theme
 * doesn't define `--popover`, and rolling a click-outside listener +
 * z-index management for a 3-line dropdown isn't worth the surface
 * area. The select resets to its placeholder option after each pick
 * so the operator can chain additions without re-clicking.
 */
function AddTranslationButton({ locales, onPick }: AddTranslationButtonProps) {
  const { t } = useTranslation();

  if (locales.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("compliance.documents.create.noMoreLocales")}
      </p>
    );
  }

  return (
    <select
      data-testid="compliance-create-document-add-translation"
      value=""
      onChange={(e) => {
        const code = e.target.value;
        if (!code) return;
        const locale = locales.find((l) => l.code === code);
        if (locale) onPick(locale);
        // Reset to placeholder so the next pick fires onChange again.
        e.target.value = "";
      }}
      className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
    >
      <option value="">+ {t("compliance.documents.create.addTranslation")}</option>
      {locales.map((l) => (
        <option key={l.code} value={l.code}>
          {l.code} — {l.displayName}
        </option>
      ))}
    </select>
  );
}
